import { Edit2, Plus, Trash2, Users, MessageSquare, Clock } from "lucide-react";
import {
  formatQuotaBannerCountdown,
  formatQuotaResetDateFrUtc,
  messagesFromQuotaPercent,
} from "../../utils/format";
import { useEffect, useState } from "react";

import { brandStyle } from "../../utils/branding";

const EMPTY_QUOTAS = {};

export default function UsersTab({
  clientInfo,
  quotas = EMPTY_QUOTAS,
  chatbotUsers,
  sumPercentsAssigned = 0,
  onEqualSplitPercents,
  equalSplitLoading = false,
  setShowUserModal,
  setEditingUserId,
  setEditUserSubclient,
  setEditUserPassword,
  setEditUserPercent,
  handleDeleteUser,
}) {
  const msgQuota = quotas.messages;
  const accessQuota = quotas.chatbot_access;
  const hasMessageQuota = Boolean(msgQuota);
  const hasAnyChatbotQuota = Boolean(msgQuota || accessQuota);
  const remainingPct = Math.max(0, 100 - sumPercentsAssigned);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    if (!msgQuota?.resets_at) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [msgQuota?.resets_at]);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {hasMessageQuota ? (
        <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-sm text-stone-800 leading-relaxed">
          <p className="font-semibold text-stone-900 mb-1.5">
            Comment fonctionne la répartition des questions ?
          </p>
          <ul className="list-disc pl-5 space-y-1 text-stone-700">
            <li>
              Votre entreprise dispose d&apos;un{" "}
              <strong>volume mensuel total</strong> de questions.
            </li>
            <li>
              Ce total est réparti entre vos accès en{" "}
              <strong>pourcentages</strong> : la somme des parts doit toujours
              faire <strong>100 %</strong>.
            </li>
            <li>
              À chaque <strong>création</strong> ou <strong>suppression</strong>{" "}
              d&apos;un accès, les parts sont <strong>rééquilibrées automatiquement</strong>{" "}
              (100 % répartis à parts égales entre tous les comptes).
            </li>
            <li>
              Le bouton <strong>« Répartir équitablement »</strong> refait la même
              répartition à la demande.
            </li>
            <li>
              Vous pouvez ensuite <strong>ajuster</strong> chaque accès avec le curseur
              ou le champ pourcentage via <strong>Modifier l&apos;accès</strong>.
            </li>
          </ul>
        </div>
      ) : null}
      {hasAnyChatbotQuota && (
        <div className="grid gap-4 sm:grid-cols-2">
          {msgQuota ? (
            <div
              className={`rounded-2xl border p-4 shadow-sm ${
                msgQuota.used >= msgQuota.max
                  ? "border-amber-200 bg-amber-50/90"
                  : msgQuota.used / msgQuota.max >= 0.9
                    ? "border-amber-100 bg-amber-50/50"
                    : "border-stone-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-stone-800">
                <MessageSquare size={18} className="text-blue-600 shrink-0" />
                Quota messages (questions)
              </div>
              <p className="mt-1 text-2xl font-bold tabular-nums text-stone-900">
                {msgQuota.used}{" "}
                <span className="text-base font-medium text-stone-500">
                  / {msgQuota.max}
                </span>
              </p>
              <p className="text-xs text-stone-500">
                Période : mois calendaire (UTC)
              </p>
              {msgQuota.resets_at ? (
                <p className="mt-2 flex flex-wrap items-center gap-x-1 text-xs text-stone-600">
                  <Clock size={14} className="shrink-0 text-stone-500" />
                  Renouvellement le{" "}
                  <strong>
                    {formatQuotaResetDateFrUtc(msgQuota.resets_at)}
                  </strong>{" "}
                  ({formatQuotaBannerCountdown(msgQuota.resets_at, nowTick)}).
                </p>
              ) : null}
            </div>
          ) : null}
          {accessQuota ? (
            <div
              className={`rounded-2xl border p-4 shadow-sm ${
                accessQuota.used >= accessQuota.max
                  ? "border-amber-200 bg-amber-50/90"
                  : accessQuota.used / accessQuota.max >= 0.9
                    ? "border-amber-100 bg-amber-50/50"
                    : "border-stone-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-stone-800">
                <Users size={18} className="text-blue-600 shrink-0" />
                Quota accès chatbot
              </div>
              <p className="mt-1 text-2xl font-bold tabular-nums text-stone-900">
                {accessQuota.used}{" "}
                <span className="text-base font-medium text-stone-500">
                  / {accessQuota.max}
                </span>
              </p>
              <p className="text-xs text-stone-500">
                Nombre maximum d&apos;utilisateurs avec accès au chatbot.
              </p>
            </div>
          ) : null}
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
        <div className="p-6 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-stone-50/50">
          <h2 className="text-xl font-semibold flex items-center gap-2 text-stone-800">
            <Users size={22} className="text-blue-500" /> Accès Chatbot (
            {chatbotUsers.length})
          </h2>
          <button
            className="text-white px-4 py-2 rounded-xl text-sm font-medium shadow-sm transition-all active:scale-[0.98] hover:brightness-95 flex items-center gap-2"
            style={brandStyle(clientInfo)}
            onClick={() => setShowUserModal(true)}
          >
            <Plus size={16} /> Créer un accès
          </button>
        </div>
        {hasMessageQuota ? (
          <div className="px-6 py-3 border-b border-stone-100 bg-stone-50/90 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-stone-700 tabular-nums leading-snug">
              <strong>{quotas.messages.max}</strong> questions / mois au total —
              il reste <strong className="text-blue-700">{remainingPct}</strong>{" "}
              % à répartir.
            </p>
            <button
              type="button"
              title="Réinitialise les pourcentages puis les répartit à parts égales (100 % au total)"
              onClick={() => onEqualSplitPercents?.()}
              disabled={
                equalSplitLoading ||
                !onEqualSplitPercents ||
                chatbotUsers.length < 1
              }
              className="shrink-0 rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-800 shadow-sm transition-all hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {equalSplitLoading ? "Répartition…" : "Répartir équitablement"}
            </button>
          </div>
        ) : null}
        {chatbotUsers.length === 0 ? (
          <div className="p-12 text-center text-stone-500">
            <div className="size-16 bg-stone-100 text-stone-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users size={32} />
            </div>
            <p className="text-lg font-medium text-stone-700 mb-1">
              Aucun accès chatbot créé
            </p>
            <p className="text-sm">
              Créez des accès pour permettre à vos utilisateurs de discuter avec
              l'IA.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200 text-xs uppercase tracking-wider text-stone-500 font-semibold">
                  <th className="px-6 py-4">Utilisateur</th>
                  <th className="px-6 py-4">Périmètre d'accès</th>
                  {hasMessageQuota ? (
                    <>
                      <th className="px-6 py-4">% quota</th>
                      <th className="px-6 py-4">Questions (mois)</th>
                    </>
                  ) : null}
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {chatbotUsers.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-stone-50/80 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="size-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 shadow-inner">
                          <span className="font-bold text-sm">
                            {u.username.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium text-stone-800">
                          {u.username}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${u.subclient_name ? "bg-brand-red/10 text-brand-red border-brand-red/20" : "bg-stone-100 text-stone-600 border-stone-200"}`}
                      >
                        {u.subclient_name || "Général (Tous)"}
                      </span>
                    </td>
                    {hasMessageQuota ? (
                      <>
                        <td className="px-6 py-4 text-sm text-stone-700">
                          {u.messages_monthly_percent != null ? (
                            <div className="tabular-nums">
                              <div className="font-medium">
                                {u.messages_monthly_percent} %
                              </div>
                              {(() => {
                                const cap =
                                  u.messages_cap ??
                                  (quotas?.messages?.max != null
                                    ? messagesFromQuotaPercent(
                                        quotas.messages.max,
                                        u.messages_monthly_percent,
                                      )
                                    : null);
                                if (cap == null) return null;
                                return (
                                  <div className="text-xs text-stone-500 mt-0.5">
                                    {cap} message{cap > 1 ? "s" : ""} / mois
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {u.messages_cap != null ? (
                            <span className="tabular-nums font-medium text-stone-800">
                              {u.messages_used_month ?? 0} / {u.messages_cap}
                            </span>
                          ) : (
                            <span className="text-stone-400">—</span>
                          )}
                          {u.quota_resets_at && u.messages_cap != null ? (
                            <div className="text-[11px] text-stone-500 mt-1 max-w-[200px] leading-snug">
                              Renouv.{" "}
                              {formatQuotaBannerCountdown(
                                u.quota_resets_at,
                                nowTick,
                              )}
                            </div>
                          ) : null}
                        </td>
                      </>
                    ) : null}

                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="p-2 text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          onClick={() => {
                            setEditUserSubclient(u.subclient_id);
                            setEditUserPassword("");
                            setEditUserPercent(
                              u.messages_monthly_percent != null
                                ? String(u.messages_monthly_percent)
                                : "",
                            );
                            setEditingUserId(u.id);
                          }}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer l'utilisateur"
                          onClick={() => handleDeleteUser(u.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
